/**
 * Viewer EchoV API
 *
 * Mirrors `window.EchoV` from `src/main_multi_model.ts` (the main app.html
 * runtime) so scripts and integrations that drive the app can also drive the
 * standalone Fragments viewer (`viewer/viewer.html`).
 *
 * Two flavours of methods are exposed:
 *
 *   1. Local methods that touch the Three.js / Fragments scene directly
 *      (visibility, transparency, highlight, camera, clipper, etc.).
 *   2. HTTP wrappers under `EchoV.api.*` that proxy the REST endpoints
 *      documented in API_DOCUMENTATION.md (cube, lightbeam, label, color,
 *      markers, status, models, folders, element info, …).
 *
 * The HTTP wrappers reuse whatever JWT is currently in `sessionStorage.token`
 * (or `localStorage.token`). When the page was opened via a `?share=<id>`
 * URL the share's `userToken` is already written to sessionStorage by
 * viewer.ts, so the same EchoV calls work both for logged-in users and for
 * unauthenticated share visitors.
 *
 * Usage (from DevTools):
 *
 *   EchoV.getModelIds()
 *   EchoV.flyToModel('xxx')
 *   EchoV.setElementsColor('xxx', [123, 456], '#ff0000')
 *   await EchoV.api.element.info('xxx', 123)
 *   await EchoV.api.cube.list()
 */

import * as THREE from "three";
import * as FRAGS from "@thatopen/fragments";

// Lightweight typing for the panel handles we pass through. We only call a
// handful of methods so we don't depend on the panels' concrete interfaces.
type PanelHandle = {
  togglePanel?: () => void;
  setPanelVisible?: (visible: boolean) => void;
  isPanelVisible?: () => boolean;
};

type ClipperHandle = PanelHandle & {
  clipper?: any;
};

export interface EchoApiOptions {
  fragments: FRAGS.FragmentsModels;
  world: any;
  scene: THREE.Scene;
  authBaseUrl: string;
  modelNames: Map<string, string>;
  panelHandles: {
    clipper?: ClipperHandle | null;
    modelsTree?: PanelHandle | null;
    sharePanel?: PanelHandle | null;
  };
  refreshFragments: () => void;
  captureState: () => any;
  applyState: (state: any) => void;
}

export interface EchoApi {
  // raw scene access (matches app.html)
  fragments: FRAGS.FragmentsModels;
  world: any;
  scene: THREE.Scene;
  camera: any;
  renderer: any;

  // models
  getAllModels(): Map<string, any>;
  getModelIds(): string[];
  getModel(id: string): any;
  getModelInfo(id: string): {
    id: string;
    name: string;
    visible: boolean;
    origin: THREE.Vector3 | null;
  } | null;
  getVisibleModels(): string[];

  // visibility
  showModel(id: string): boolean;
  hideModel(id: string): boolean;
  toggleModel(id: string): boolean | null;
  showAllModels(): void;
  hideAllModels(): void;

  // camera
  getCameraState(): {
    position: [number, number, number];
    target: [number, number, number];
  };
  setCameraState(
    state: { position?: [number, number, number]; target?: [number, number, number] },
    animate?: boolean
  ): void;
  flyToModel(id: string, animate?: boolean): boolean;
  fitToAll(animate?: boolean): boolean;

  // origin
  getModelOrigin(id: string): THREE.Vector3 | null;
  setModelOrigin(id: string, x?: number, y?: number, z?: number): THREE.Vector3 | null;
  adjustModelOrigin(id: string, dx?: number, dy?: number, dz?: number): THREE.Vector3 | null;

  // transparency
  setModelTransparency(id: string, opacity: number): boolean;
  setAllModelsTransparency(opacity: number): void;

  // color / highlight (local FRAGS)
  setElementsColor(modelId: string, localIds: number[], color: string, opacity?: number): Promise<void>;
  resetElementsColor(modelId: string, localIds?: number[]): Promise<void>;
  setCategoryColor(modelId: string, category: string, color: string, opacity?: number): Promise<number>;
  getCategories(modelId: string): Promise<string[]>;
  getCategoryElements(modelId: string, category: string): Promise<number[]>;

  // element data (local)
  getElementData(modelId: string, localIds: number[], opts?: any): Promise<any[]>;

  // HTTP REST proxies — same backend as app.html
  api: {
    element: {
      info(modelId: string, localId: number): Promise<any>;
      location(modelId: string, localId: number): Promise<any>;
      boundingBox(modelId: string, localId: number): Promise<any>;
      export(modelId: string): Promise<any>;
    };
    cube: {
      add(data: any): Promise<any>;
      list(): Promise<any>;
      deleteAll(): Promise<any>;
      create(modelId: string, localId: number, data: any): Promise<any>;
      update(modelId: string, localId: number, data: any): Promise<any>;
      delete(modelId: string, localId: number): Promise<any>;
    };
    lightBeam: {
      add(modelId: string, localId: number, data: any): Promise<any>;
      update(modelId: string, localId: number, data: any): Promise<any>;
      delete(modelId: string, localId: number): Promise<any>;
      list(): Promise<any>;
    };
    label: {
      add(modelId: string, localId: number, data: any): Promise<any>;
      update(modelId: string, localId: number, data: any): Promise<any>;
      delete(modelId: string, localId: number): Promise<any>;
    };
    color: {
      set(data: any): Promise<any>;
      reset(data: any): Promise<any>;
      batch(data: any): Promise<any>;
    };
    boundingBoxHighlight: {
      get(modelId: string, localId: number): Promise<any>;
      set(modelId: string, localId: number, data: any): Promise<any>;
    };
    markers: {
      list(folderId?: string): Promise<any>;
      create(data: any): Promise<any>;
      update(id: string, data: any): Promise<any>;
      delete(id: string): Promise<any>;
    };
    status: {
      list(): Promise<any>;
      get(folderId: string): Promise<any>;
      set(folderId: string, data: any): Promise<any>;
      setBatch(data: any): Promise<any>;
      delete(folderId: string): Promise<any>;
    };
    userModels: { list(): Promise<any> };
    folders: { list(): Promise<any> };
    raw(path: string, options?: RequestInit): Promise<any>;
  };

  // clipper
  clipper: {
    handle: ClipperHandle | null | undefined;
    list(): any;
    addPlane(point: [number, number, number], normal: [number, number, number]): string | null;
    deleteAll(): void;
  };

  // panels
  panels: {
    toggleClipper(): void;
    toggleModelsTree(): void;
    toggleShare(): void;
    closeAll(): void;
  };

  // share state
  share: {
    capture(): any;
    apply(state: any): void;
  };

  refresh(): void;
  version: string;
}

/**
 * Install `window.EchoV` for the viewer page. Returns the same handle so
 * callers can stash it for tests / debugging.
 */
export function installEchoApi(opts: EchoApiOptions): EchoApi {
  const {
    fragments,
    world,
    scene,
    authBaseUrl,
    modelNames,
    panelHandles,
    refreshFragments,
    captureState,
    applyState,
  } = opts;

  // ---------- helpers ----------
  const getModelObject = (id: string): THREE.Object3D | undefined => {
    const m: any = fragments.models.list.get(id);
    return m?.object;
  };

  const getAuthHeaders = (): HeadersInit => {
    const token =
      sessionStorage.getItem("token") ||
      localStorage.getItem("token") ||
      "";
    const base: Record<string, string> = { "Content-Type": "application/json" };
    if (token) base.Authorization = `Bearer ${token}`;
    return base;
  };

  const apiCall = async (path: string, options: RequestInit = {}) => {
    const url = `${authBaseUrl}${path.startsWith("/") ? path : "/" + path}`;
    const headers = { ...getAuthHeaders(), ...((options.headers as any) || {}) };
    const resp = await fetch(url, { ...options, headers });
    if (!resp.ok) {
      let body = "";
      try {
        body = await resp.text();
      } catch {
        /* noop */
      }
      throw new Error(
        `HTTP ${resp.status} ${resp.statusText}${body ? ": " + body.slice(0, 240) : ""}`
      );
    }
    const ct = resp.headers.get("content-type") || "";
    return ct.includes("application/json") ? resp.json() : resp.text();
  };

  // ---------- models / visibility ----------
  const getAllModels = () => fragments.models.list as Map<string, any>;
  const getModelIds = () => Array.from(fragments.models.list.keys());
  const getModel = (id: string) => fragments.models.list.get(id);

  const getModelInfo = (id: string) => {
    const m: any = fragments.models.list.get(id);
    if (!m) return null;
    const obj = m.object as THREE.Object3D | undefined;
    return {
      id,
      name: modelNames.get(id) || id,
      visible: obj ? obj.visible !== false : true,
      origin: obj ? obj.position.clone() : null,
    };
  };

  const getVisibleModels = () =>
    getModelIds().filter((id) => {
      const o = getModelObject(id);
      return o ? o.visible !== false : false;
    });

  const showModel = (id: string) => {
    const o = getModelObject(id);
    if (!o) return false;
    o.visible = true;
    refreshFragments();
    return true;
  };
  const hideModel = (id: string) => {
    const o = getModelObject(id);
    if (!o) return false;
    o.visible = false;
    refreshFragments();
    return true;
  };
  const toggleModel = (id: string) => {
    const o = getModelObject(id);
    if (!o) return null;
    o.visible = !o.visible;
    refreshFragments();
    return o.visible;
  };
  const showAllModels = () => {
    fragments.models.list.forEach((m: any) => {
      if (m?.object) m.object.visible = true;
    });
    refreshFragments();
  };
  const hideAllModels = () => {
    fragments.models.list.forEach((m: any) => {
      if (m?.object) m.object.visible = false;
    });
    refreshFragments();
  };

  // ---------- camera ----------
  const getCameraState = () => {
    const pos = new THREE.Vector3();
    const tgt = new THREE.Vector3();
    try {
      (world.camera.controls as any).getPosition(pos);
    } catch {
      /* noop */
    }
    try {
      (world.camera.controls as any).getTarget(tgt);
    } catch {
      /* noop */
    }
    return {
      position: [pos.x, pos.y, pos.z] as [number, number, number],
      target: [tgt.x, tgt.y, tgt.z] as [number, number, number],
    };
  };

  const setCameraState = (
    state: { position?: [number, number, number]; target?: [number, number, number] },
    animate = true
  ) => {
    if (!state.position || !state.target) return;
    try {
      (world.camera.controls as any).setLookAt(
        state.position[0], state.position[1], state.position[2],
        state.target[0], state.target[1], state.target[2],
        !!animate
      );
    } catch (e) {
      console.warn("[EchoV] setCameraState failed", e);
    }
  };

  const flyToModel = (id: string, animate = true) => {
    const o = getModelObject(id);
    if (!o) return false;
    const box = new THREE.Box3().setFromObject(o);
    if (box.isEmpty()) return false;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    try {
      (world.camera.controls as any).fitToSphere(sphere, !!animate);
      return true;
    } catch (e) {
      console.warn("[EchoV] flyToModel failed", e);
      return false;
    }
  };

  const fitToAll = (animate = true) => {
    const box = new THREE.Box3();
    fragments.models.list.forEach((m: any) => {
      if (m?.object && m.object.visible !== false) box.expandByObject(m.object);
    });
    if (box.isEmpty()) return false;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    try {
      (world.camera.controls as any).fitToSphere(sphere, !!animate);
      return true;
    } catch (e) {
      console.warn("[EchoV] fitToAll failed", e);
      return false;
    }
  };

  // ---------- origin / transform ----------
  const getModelOrigin = (id: string) => {
    const o = getModelObject(id);
    return o ? o.position.clone() : null;
  };
  const setModelOrigin = (id: string, x?: number, y?: number, z?: number) => {
    const o = getModelObject(id);
    if (!o) return null;
    if (x !== undefined) o.position.x = x;
    if (y !== undefined) o.position.y = y;
    if (z !== undefined) o.position.z = z;
    o.updateMatrix();
    o.updateMatrixWorld(true);
    refreshFragments();
    return o.position.clone();
  };
  const adjustModelOrigin = (
    id: string,
    dx?: number,
    dy?: number,
    dz?: number
  ) => {
    const o = getModelObject(id);
    if (!o) return null;
    if (dx) o.position.x += dx;
    if (dy) o.position.y += dy;
    if (dz) o.position.z += dz;
    o.updateMatrix();
    o.updateMatrixWorld(true);
    refreshFragments();
    return o.position.clone();
  };

  // ---------- transparency ----------
  const setModelTransparency = (id: string, opacity: number) => {
    const o = getModelObject(id);
    if (!o) return false;
    o.traverse((child: THREE.Object3D) => {
      const mesh = child as THREE.Mesh;
      const mat = (mesh as any).material;
      if (!mat) return;
      const mats = Array.isArray(mat) ? mat : [mat];
      mats.forEach((m: any) => {
        m.transparent = opacity < 1;
        m.opacity = opacity;
        m.needsUpdate = true;
      });
    });
    refreshFragments();
    return true;
  };
  const setAllModelsTransparency = (opacity: number) => {
    fragments.models.list.forEach((_: any, id: string) =>
      setModelTransparency(id, opacity)
    );
  };

  // ---------- color / highlight (local FRAGS) ----------
  const setElementsColor = async (
    modelId: string,
    localIds: number[],
    color: string,
    opacity = 1
  ) => {
    const m: any = fragments.models.list.get(modelId);
    if (!m) throw new Error(`Model not found: ${modelId}`);
    const matDef: FRAGS.MaterialDefinition = {
      color: new THREE.Color(color),
      renderedFaces: FRAGS.RenderedFaces.TWO,
      opacity,
      transparent: opacity < 1,
    };
    await m.highlight(localIds, matDef);
    refreshFragments();
  };

  const resetElementsColor = async (modelId: string, localIds?: number[]) => {
    const m: any = fragments.models.list.get(modelId);
    if (!m) throw new Error(`Model not found: ${modelId}`);
    if (localIds && localIds.length > 0) {
      await m.resetHighlight(localIds);
    } else {
      // Best-effort full reset. FRAGS.resetHighlight() with no args clears all.
      try {
        await m.resetHighlight();
      } catch (e) {
        console.warn("[EchoV] resetHighlight() with no args failed", e);
      }
    }
    refreshFragments();
  };

  const setCategoryColor = async (
    modelId: string,
    category: string,
    color: string,
    opacity = 1
  ) => {
    const m: any = fragments.models.list.get(modelId);
    if (!m) throw new Error(`Model not found: ${modelId}`);
    const itemsMap = await m.getItemsOfCategories([
      new RegExp(`^${category}$`),
    ]);
    const ids: number[] = [];
    Object.values(itemsMap || {}).forEach((arr: any) => {
      if (Array.isArray(arr)) ids.push(...arr);
    });
    if (ids.length > 0) await setElementsColor(modelId, ids, color, opacity);
    return ids.length;
  };

  const getCategories = async (modelId: string) => {
    const m: any = fragments.models.list.get(modelId);
    if (!m) return [];
    return await m.getCategories();
  };

  const getCategoryElements = async (modelId: string, category: string) => {
    const m: any = fragments.models.list.get(modelId);
    if (!m) return [];
    const itemsMap = await m.getItemsOfCategories([
      new RegExp(`^${category}$`),
    ]);
    const ids: number[] = [];
    Object.values(itemsMap || {}).forEach((arr: any) => {
      if (Array.isArray(arr)) ids.push(...arr);
    });
    return ids;
  };

  // ---------- element data (local) ----------
  const getElementData = async (
    modelId: string,
    localIds: number[],
    extra?: any
  ) => {
    const m: any = fragments.models.list.get(modelId);
    if (!m) return [];
    return await m.getItemsData(localIds, extra || { attributesDefault: true });
  };

  // ---------- HTTP REST proxies ----------
  const cube = {
    add: (data: any) =>
      apiCall("/api/cube/add", { method: "POST", body: JSON.stringify(data) }),
    list: () => apiCall("/api/cube/list"),
    deleteAll: () => apiCall("/api/cube/all", { method: "DELETE" }),
    create: (modelId: string, localId: number, data: any) =>
      apiCall(`/api/localid/${modelId}/${localId}/create-cube`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (modelId: string, localId: number, data: any) =>
      apiCall(`/api/localid/${modelId}/${localId}/cube`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (modelId: string, localId: number) =>
      apiCall(`/api/localid/${modelId}/${localId}/cube`, { method: "DELETE" }),
  };

  const lightBeam = {
    add: (modelId: string, localId: number, data: any) =>
      apiCall(`/api/lightbeam/${modelId}/${localId}`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (modelId: string, localId: number, data: any) =>
      apiCall(`/api/lightbeam/${modelId}/${localId}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (modelId: string, localId: number) =>
      apiCall(`/api/lightbeam/${modelId}/${localId}`, { method: "DELETE" }),
    list: () => apiCall("/api/lightbeam/list"),
  };

  const label = {
    add: (modelId: string, localId: number, data: any) =>
      apiCall(`/api/label/${modelId}/${localId}`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (modelId: string, localId: number, data: any) =>
      apiCall(`/api/label/${modelId}/${localId}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (modelId: string, localId: number) =>
      apiCall(`/api/label/${modelId}/${localId}`, { method: "DELETE" }),
  };

  const colorApi = {
    set: (data: any) =>
      apiCall("/api/color/set", { method: "POST", body: JSON.stringify(data) }),
    reset: (data: any) =>
      apiCall("/api/color/reset", { method: "POST", body: JSON.stringify(data) }),
    batch: (data: any) =>
      apiCall("/api/color/batch", { method: "POST", body: JSON.stringify(data) }),
  };

  const boundingBoxHighlight = {
    get: (modelId: string, localId: number) =>
      apiCall(`/api/boundingbox/${modelId}/${localId}/highlight`),
    set: (modelId: string, localId: number, data: any) =>
      apiCall(`/api/boundingbox/${modelId}/${localId}/highlight`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  };

  const markers = {
    list: (folderId?: string) =>
      apiCall(folderId ? `/api/markers?folder=${folderId}` : "/api/markers"),
    create: (data: any) =>
      apiCall("/api/markers", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: any) =>
      apiCall(`/api/markers/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      apiCall(`/api/markers/${id}`, { method: "DELETE" }),
  };

  const status = {
    list: () => apiCall("/api/status"),
    get: (folderId: string) => apiCall(`/api/status/${folderId}`),
    set: (folderId: string, data: any) =>
      apiCall(`/api/status/${folderId}`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    setBatch: (data: any) =>
      apiCall("/api/status/batch", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    delete: (folderId: string) =>
      apiCall(`/api/status/${folderId}`, { method: "DELETE" }),
  };

  const userModels = { list: () => apiCall("/api/models") };
  const folders = { list: () => apiCall("/api/folders") };

  const elementHttp = {
    info: (modelId: string, localId: number) =>
      apiCall(`/api/localid/${modelId}/${localId}/info`),
    location: (modelId: string, localId: number) =>
      apiCall(`/api/localid/${modelId}/${localId}/estimate-location`),
    boundingBox: (modelId: string, localId: number) =>
      apiCall(`/api/boundingbox/${modelId}/${localId}`),
    export: (modelId: string) => apiCall(`/api/localid/${modelId}/export`),
  };

  // ---------- clipper ----------
  const clipperApi = {
    handle: panelHandles.clipper ?? null,
    list: () => panelHandles.clipper?.clipper?.list,
    addPlane: (
      point: [number, number, number],
      normal: [number, number, number]
    ) => {
      const cl = panelHandles.clipper?.clipper;
      if (!cl) return null;
      const p = new THREE.Vector3(point[0], point[1], point[2]);
      const n = new THREE.Vector3(normal[0], normal[1], normal[2]).normalize();
      try {
        return cl.createFromNormalAndCoplanarPoint(world, n, p) || null;
      } catch (e) {
        console.warn("[EchoV] clipper.addPlane failed", e);
        return null;
      }
    },
    deleteAll: () => {
      const cl = panelHandles.clipper?.clipper;
      try {
        cl?.deleteAll?.();
      } catch (e) {
        console.warn("[EchoV] clipper.deleteAll failed", e);
      }
    },
  };

  // ---------- panels ----------
  const panels = {
    toggleClipper: () => panelHandles.clipper?.togglePanel?.(),
    toggleModelsTree: () => panelHandles.modelsTree?.togglePanel?.(),
    toggleShare: () => panelHandles.sharePanel?.togglePanel?.(),
    closeAll: () => {
      panelHandles.clipper?.setPanelVisible?.(false);
      panelHandles.modelsTree?.setPanelVisible?.(false);
      panelHandles.sharePanel?.setPanelVisible?.(false);
    },
  };

  // ---------- share ----------
  const share = {
    capture: () => captureState(),
    apply: (state: any) => applyState(state),
  };

  const api: EchoApi = {
    fragments,
    world,
    scene,
    camera: world.camera,
    renderer: world.renderer,

    getAllModels,
    getModelIds,
    getModel,
    getModelInfo,
    getVisibleModels,

    showModel,
    hideModel,
    toggleModel,
    showAllModels,
    hideAllModels,

    getCameraState,
    setCameraState,
    flyToModel,
    fitToAll,

    getModelOrigin,
    setModelOrigin,
    adjustModelOrigin,

    setModelTransparency,
    setAllModelsTransparency,

    setElementsColor,
    resetElementsColor,
    setCategoryColor,
    getCategories,
    getCategoryElements,

    getElementData,

    api: {
      element: elementHttp,
      cube,
      lightBeam,
      label,
      color: colorApi,
      boundingBoxHighlight,
      markers,
      status,
      userModels,
      folders,
      raw: apiCall,
    },

    clipper: clipperApi,
    panels,
    share,

    refresh: refreshFragments,
    version: "viewer-1.0",
  };

  (window as any).EchoV = api;
  console.log(
    "%c✅ EchoV API installed (viewer)",
    "color:#44d3de;font-weight:bold",
    api
  );
  return api;
}
